import { createContext, useContext, useState, type ReactNode } from "react";
import { CardModal } from "../components/dashboard/CardModal";

/**
 * Lets any card request to be expanded into a full-screen modal. The card
 * passes a title and a render function that produces its expanded body. The
 * provider owns the single modal instance, so only one card is ever expanded
 * at a time and we never mount two Three.js scenes etc.
 */
type ExpandPayload = { title: string; render: () => ReactNode };

type Ctx = {
  expand: (payload: ExpandPayload) => void;
  close: () => void;
};

const ExpandContext = createContext<Ctx>({ expand: () => {}, close: () => {} });

export function CardExpandProvider({ children }: { children: ReactNode }) {
  const [payload, setPayload] = useState<ExpandPayload | null>(null);

  return (
    <ExpandContext.Provider value={{ expand: setPayload, close: () => setPayload(null) }}>
      {children}
      <CardModal
        open={!!payload}
        onClose={() => setPayload(null)}
        title={payload?.title ?? ""}
      >
        {payload?.render()}
      </CardModal>
    </ExpandContext.Provider>
  );
}

export const useCardExpand = () => useContext(ExpandContext);
